<template>
  <span
    class="kds-chip"
    :class="[`kds-chip--${resolvedTone}`, { 'kds-chip--live': live }]"
  >
    <span
      v-if="resolvedTone === 'persona' && segment"
      class="kds-chip__dot"
      :style="{ background: dotColor }"
    />
    <span
      v-else-if="live"
      class="kds-chip__dot kds-chip__dot--live kds-pulse-dot"
    />
    <span class="kds-chip__label">
      <slot />
    </span>
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { SEGMENT_TOKEN } from '@/kds/tokens';
import type { KdsChipTone, PersonaSegment } from '@/kds/types';

interface Props {
  tone?: KdsChipTone;
  segment?: PersonaSegment;
  live?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  tone: 'neutral',
  segment: undefined,
  live: false
});

const KNOWN: KdsChipTone[] = ['neutral', 'persona', 'live'];

const resolvedTone = computed<KdsChipTone>(() =>
  KNOWN.includes(props.tone) ? props.tone : 'neutral'
);

const dotColor = computed(() =>
  props.segment ? SEGMENT_TOKEN[props.segment] : 'var(--stone)'
);
</script>

<style scoped lang="scss">
.kds-chip {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: 4px 10px;
  font-family: var(--font-ui);
  font-size: 12.5px;
  line-height: 1.2;
  border-radius: var(--r-pill);
  border: var(--bw) solid var(--mist);
  background: var(--paper-2);
  color: var(--ink);

  &--neutral {
    background: var(--paper-2);
    border-color: var(--mist);
  }

  &--persona {
    background: var(--paper);
    border-color: var(--mist);
  }

  &--live,
  &.kds-chip--live {
    background: var(--signal-tint);
    border-color: var(--signal-press);
    color: var(--ink);
  }

  &__dot {
    width: 6px;
    height: 6px;
    border-radius: var(--r-pill);
    flex-shrink: 0;
  }

  &__dot--live {
    background: var(--signal);
  }

  &__label {
    white-space: nowrap;
  }
}
</style>
